import { useGetSignup } from '@repo/api/auth/useGetSignup';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { getUserLvl } from '@repo/lib/user-lvl/getUserLvl';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { SignupForm } from '@/components/features/auth/SignupForm';
import UserAvatarEditor from '@/components/features/user/UserAvatarEditor';
import { AvatarEdit, AvatarImage } from '@/components/ui/avatar';
import AuthView from '@/layout/AuthView';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';
import { useCredentials } from '@/store/auth-store';

export default function SignupPage() {
  const [openEditAvatar, onOpenEditAvatarChange] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const authToken: string | undefined = searchParams.get('t') ?? undefined;
  const { data: signupData, isFetching, isSuccess, isError, error } = useGetSignup(authToken);
  const userRole = getUserLvl(signupData?.lvl);
  const { avatarURL } = useCredentials();

  useEffect(() => {
    if (isError) {
      toasterMessageHandler(baseErrorMessages, error);
      navigate('/login');
    }
  }, [isError]);

  return (
    <AuthView>
      <UserAvatarEditor open={openEditAvatar} onOpenChange={onOpenEditAvatarChange} />
      <div
        className={
          'border-border-primary bg-background relative z-10 flex min-h-[calc(100dvh-var(--spacing)*6)] w-full flex-col gap-7 overflow-auto rounded-2xl rounded-b-none border px-4 pt-6 pb-11 md:min-h-fit md:w-[clamp(22rem,50dvw,26rem)] md:rounded-b-2xl md:border-4 md:px-4 md:pt-4 md:pb-4 md:shadow-lg'
        }
      >
        <div
          data-fetching={isFetching}
          className={'bg-background/50 absolute inset-0 z-20 m-auto flex flex-wrap items-center justify-center gap-3 data-[fetching=false]:hidden data-[fetching=true]:backdrop-blur-xs'}
        >
          <Loader2 className={'size-4 animate-spin'} aria-hidden={'true'} />
          <h5 className={'font-medium'}>Ładowanie danych...</h5>
        </div>
        <header className={'flex w-full flex-col items-center gap-4 py-1 text-center'}>
          <img src={'/logo.png'} alt={'Better Call Paul logo'} className={'size-12 object-contain'} />
          <span aria-hidden className="bg-brand-accent dark:bg-brand-accent/75 absolute -top-18 -z-10 size-60 rounded-full blur-2xl" />

          <div className={'flex w-full flex-col gap-1'}>
            <h1 className={'text-foreground text-2xl font-semibold'}>Zarejestruj się</h1>
            <p className={'text-muted-foreground text-sm'}>Kilka kroków dzieli Cię od pełnego dostępu.</p>
          </div>
        </header>

        <div className={'flex w-full items-center gap-3'}>
          <AvatarEdit className={'size-20'} onClick={() => onOpenEditAvatarChange(true)}>
            <AvatarImage src={avatarURL ?? undefined} alt="user-avatar" />
          </AvatarEdit>
          {isSuccess && (
            <div className={'flex flex-col gap-0.5'}>
              <p className={'text-foreground text-base font-medium'}>{`${signupData?.name} ${signupData?.surname}`}</p>
              <p className={'text-muted-foreground text-sm'}>{userRole}</p>
            </div>
          )}
        </div>
        <div className={'border-border-primary w-full border-t'} />
        <div className={'flex w-full grow flex-col'}>
          <SignupForm authToken={authToken} signupData={signupData} />
        </div>
      </div>
    </AuthView>
  );
}
